import React from 'react';

import {
    getStoredSession,
    getUser,
    setStoredSession,
    signIn,
    signOut,
    signUp,
    updateUser
} from '../../lib/supabase-auth.js';

import styles from './auth-app.css';

const routeMap = {
    '/login': 'login',
    '/signup': 'signup',
    '/settings': 'settings'
};

const navigate = path => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new Event('popstate'));
};

const goHome = () => {
    window.location.assign('/');
};

const AuthApp = () => {
    const [pathname, setPathname] = React.useState(() => window.location.pathname.replace(/\/$/, '') || '/');
    const [session, setSession] = React.useState(null);
    const [loadingSession, setLoadingSession] = React.useState(true);
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');
    const [notice, setNotice] = React.useState('');

    const [loginForm, setLoginForm] = React.useState({
        email: '',
        password: ''
    });

    const [signupForm, setSignupForm] = React.useState({
        name: '',
        username: '',
        email: '',
        password: ''
    });

    const [settingsForm, setSettingsForm] = React.useState({
        name: '',
        username: ''
    });

    React.useEffect(() => {
        const handlePopState = () => setPathname(window.location.pathname.replace(/\/$/, '') || '/');
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    React.useEffect(() => {
        const bootstrap = async () => {
            const storedSession = getStoredSession();
            if (!storedSession || !storedSession.access_token) {
                setLoadingSession(false);
                return;
            }

            try {
                const user = await getUser(storedSession.access_token);
                const nextSession = Object.assign({}, storedSession, {user});
                setSession(nextSession);
                setStoredSession(nextSession);
                setSettingsForm({
                    name: user?.user_metadata?.name || '',
                    username: user?.user_metadata?.username || ''
                });
            } catch (err) {
                setError('Your session expired. Please log in again.');
                setSession(null);
                setStoredSession(null);
            } finally {
                setLoadingSession(false);
            }
        };

        bootstrap();
    }, []);

    React.useEffect(() => {
        if (!session) return;
        setSettingsForm({
            name: session.user?.user_metadata?.name || '',
            username: session.user?.user_metadata?.username || ''
        });
    }, [session]);

    const currentRoute = routeMap[pathname] || 'home';

    React.useEffect(() => {
        if (session && (currentRoute === 'login' || currentRoute === 'signup')) {
            goHome();
        }
    }, [currentRoute, session]);

    const handleEmailPasswordChange = field => event => {
        const value = event.target.value;
        setLoginForm(prev => Object.assign({}, prev, {[field]: value}));
    };

    const handleSignupChange = field => event => {
        const value = event.target.value;
        setSignupForm(prev => Object.assign({}, prev, {[field]: value}));
    };

    const handleSettingsChange = field => event => {
        const value = event.target.value;
        setSettingsForm(prev => Object.assign({}, prev, {[field]: value}));
    };

    const handleLogin = async event => {
        event.preventDefault();
        setSubmitting(true);
        setError('');
        setNotice('');
        try {
            const authSession = await signIn(loginForm);
            const user = authSession.user || await getUser(authSession.access_token);
            const nextSession = Object.assign({}, authSession, {user});
            setStoredSession(nextSession);
            setSession(nextSession);
            setLoginForm({
                email: '',
                password: ''
            });
            goHome();
        } catch (err) {
            setError(err.message || 'Unable to log in right now.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSignup = async event => {
        event.preventDefault();
        setSubmitting(true);
        setError('');
        setNotice('');

        try {
            const authSession = await signUp(signupForm);
            const nextSession = authSession.session || authSession;
            const user = nextSession.user || authSession.user;

            if (!nextSession || !nextSession.access_token) {
                setNotice('Account created. Check your email to finish signing in, then return to log in.');
                setSignupForm({
                    name: '',
                    username: '',
                    email: '',
                    password: ''
                });
                navigate('/login');
                return;
            }

            const hydratedUser = user || await getUser(nextSession.access_token);
            const sessionWithUser = Object.assign({}, nextSession, {user: hydratedUser});
            setStoredSession(sessionWithUser);
            setSession(sessionWithUser);
            setSignupForm({
                name: '',
                username: '',
                email: '',
                password: ''
            });
            goHome();
        } catch (err) {
            setError(err.message || 'Unable to sign up right now.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveSettings = async event => {
        event.preventDefault();
        if (!session || !session.access_token) return;

        setSubmitting(true);
        setError('');
        setNotice('');
        try {
            const existingMetadata = session.user?.user_metadata || {};
            const updatedUserPayload = await updateUser(session.access_token, {
                ...existingMetadata,
                name: settingsForm.name,
                username: settingsForm.username,
                credits: existingMetadata.credits ?? 200
            });
            const updatedUser = updatedUserPayload?.user || updatedUserPayload;

            const nextSession = Object.assign({}, session, {user: updatedUser});
            setSession(nextSession);
            setStoredSession(nextSession);
            setNotice('Saved your profile.');
        } catch (err) {
            setError(err.message || 'Unable to save settings.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleLogout = async () => {
        if (session?.access_token) {
            await signOut(session.access_token);
        } else {
            setStoredSession(null);
        }
        setSession(null);
        navigate('/login');
    };

    const userName = session?.user?.user_metadata?.name || session?.user?.user_metadata?.username || 'Creator';

    return (
        <div className={styles.page}>
            <div className={styles.glowOne} />
            <div className={styles.glowTwo} />
            <main className={styles.shell}>
                <header className={styles.topBar}>
                    <button
                        className={styles.brand}
                        type="button"
                        onClick={goHome}
                    >
                        <img className={styles.brandMark} src="/static/favicon.svg" alt="" aria-hidden="true" />
                        BitByte
                    </button>
                    <div className={styles.topLinks}>
                        {currentRoute === 'login' ? (
                            <button
                                className={styles.topLink}
                                type="button"
                                onClick={() => navigate('/signup')}
                            >
                                Sign up
                            </button>
                        ) : null}
                        {currentRoute === 'signup' ? (
                            <button
                                className={styles.topLink}
                                type="button"
                                onClick={() => navigate('/login')}
                            >
                                Log in
                            </button>
                        ) : null}
                        {currentRoute === 'settings' ? (
                            <button
                                className={styles.topLink}
                                type="button"
                                onClick={goHome}
                            >
                                Home
                            </button>
                        ) : null}
                    </div>
                </header>

                <section className={styles.hero}>
                    <div className={styles.heroCopy}>
                        <h1 className={styles.title}>
                            {currentRoute === 'signup' ? 'Create your BitByte account' : currentRoute === 'settings' ? 'Settings' : 'Welcome back'}
                        </h1>
                    </div>
                </section>

                {loadingSession ? (
                    <div className={styles.loadingCard}>Checking your session...</div>
                ) : null}

                {error ? (
                    <div className={styles.alertError}>{error}</div>
                ) : null}

                {notice ? (
                    <div className={styles.alertNotice}>{notice}</div>
                ) : null}

                {currentRoute === 'login' ? (
                    <form className={styles.card} onSubmit={handleLogin}>
                        <div className={styles.cardHeader}>
                            <h2 className={styles.cardTitle}>Log in</h2>
                            <p className={styles.cardSubtitle}>Use your email and password.</p>
                        </div>

                        <label className={styles.fieldLabel} htmlFor="login-email">Email</label>
                        <input
                            id="login-email"
                            className={styles.input}
                            type="email"
                            value={loginForm.email}
                            onChange={handleEmailPasswordChange('email')}
                            autoComplete="email"
                            required
                        />

                        <label className={styles.fieldLabel} htmlFor="login-password">Password</label>
                        <input
                            id="login-password"
                            className={styles.input}
                            type="password"
                            value={loginForm.password}
                            onChange={handleEmailPasswordChange('password')}
                            autoComplete="current-password"
                            required
                        />

                        <div className={styles.buttonRow}>
                            <button className={styles.primaryButton} disabled={submitting} type="submit">
                                {submitting ? 'Logging in...' : 'Log in'}
                            </button>
                            <button className={styles.secondaryButton} type="button" onClick={() => navigate('/signup')}>
                                Sign up
                            </button>
                        </div>
                    </form>
                ) : null}

                {currentRoute === 'signup' ? (
                    <form className={styles.card} onSubmit={handleSignup}>
                        <div className={styles.cardHeader}>
                            <h2 className={styles.cardTitle}>Sign up</h2>
                        </div>

                        <div className={styles.gridTwo}>
                            <div>
                                <label className={styles.fieldLabel} htmlFor="signup-name">Name</label>
                                <input
                                    id="signup-name"
                                    className={styles.input}
                                    type="text"
                                    value={signupForm.name}
                                    onChange={handleSignupChange('name')}
                                    autoComplete="name"
                                    required
                                />
                            </div>
                            <div>
                                <label className={styles.fieldLabel} htmlFor="signup-username">Username</label>
                                <input
                                    id="signup-username"
                                    className={styles.input}
                                    type="text"
                                    value={signupForm.username}
                                    onChange={handleSignupChange('username')}
                                    autoComplete="username"
                                    required
                                />
                            </div>
                        </div>

                        <label className={styles.fieldLabel} htmlFor="signup-email">Email</label>
                        <input
                            id="signup-email"
                            className={styles.input}
                            type="email"
                            value={signupForm.email}
                            onChange={handleSignupChange('email')}
                            autoComplete="email"
                            required
                        />

                        <label className={styles.fieldLabel} htmlFor="signup-password">Password</label>
                        <input
                            id="signup-password"
                            className={styles.input}
                            type="password"
                            value={signupForm.password}
                            onChange={handleSignupChange('password')}
                            autoComplete="new-password"
                            required
                        />

                        <div className={styles.buttonRow}>
                            <button className={styles.primaryButton} disabled={submitting} type="submit">
                                {submitting ? 'Creating account...' : 'Create account'}
                            </button>
                            <button className={styles.secondaryButton} type="button" onClick={() => navigate('/login')}>
                                Log in
                            </button>
                        </div>
                    </form>
                ) : null}

                {currentRoute === 'settings' ? (
                    session ? (
                        <form className={styles.card} onSubmit={handleSaveSettings}>
                            <div className={styles.cardHeader}>
                                <h2 className={styles.cardTitle}>Settings</h2>
                            </div>

                            <div className={styles.profileSummary}>
                                <div className={styles.profileChip}>
                                    <span className={styles.profileChipLabel}>Signed in as</span>
                                    <span className={styles.profileChipValue}>{userName}</span>
                                </div>
                            </div>

                            <div className={styles.gridTwo}>
                                <div>
                                    <label className={styles.fieldLabel} htmlFor="settings-name">Name</label>
                                    <input
                                        id="settings-name"
                                        className={styles.input}
                                        type="text"
                                        value={settingsForm.name}
                                        onChange={handleSettingsChange('name')}
                                    />
                                </div>
                                <div>
                                    <label className={styles.fieldLabel} htmlFor="settings-username">Username</label>
                                    <input
                                        id="settings-username"
                                        className={styles.input}
                                        type="text"
                                        value={settingsForm.username}
                                        onChange={handleSettingsChange('username')}
                                    />
                                </div>
                            </div>

                            <div className={styles.buttonRow}>
                                <button className={styles.primaryButton} disabled={submitting} type="submit">
                                    {submitting ? 'Saving...' : 'Save changes'}
                                </button>
                                <button className={styles.secondaryButton} type="button" onClick={handleLogout}>
                                    Log out
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <h2 className={styles.cardTitle}>Settings locked</h2>
                                <p className={styles.cardSubtitle}>Please log in to view your settings.</p>
                            </div>
                            <button className={styles.primaryButton} type="button" onClick={() => navigate('/login')}>
                                Go to login
                            </button>
                        </div>
                    )
                ) : null}
            </main>
        </div>
    );
};

export default AuthApp;
